import { isApiError } from '@pa/api'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useRouter } from '@tanstack/react-router'
import { useId, useState, type SyntheticEvent } from 'react'
import { useAuthStore } from '../auth/AuthProvider'
import { isAuthError } from '../auth/client'
import { safeRedirect } from '../auth/redirect'
import { meQueryKey, meQueryOptions } from '../me/me'
import { AuthBanner, AuthCard, AuthTitle } from './AuthCard'
import { PasswordField } from './PasswordField'

/** One message for every credential problem, so A-01 never reveals which accounts exist (UserFlows A-01). */
export const SIGN_IN_GENERIC_ERROR = 'Incorrect email or password.'
export const SIGN_IN_TOO_MANY = 'Too many attempts. Try again in a few minutes.'

const REASONS: Record<string, string> = {
  'session-expired': 'Please sign in again',
  reauth: 'Please sign in again to set your password',
  /** After A-03 (UserFlows A-03). */
  'password-updated': 'Password updated',
}

/** Shown as an error: the clinic was deactivated (UserFlows A-01; P-10). */
export const CLINIC_INACTIVE_MESSAGE = "This clinic's account is not active. Contact support."
const ERROR_REASONS: Record<string, string> = { 'clinic-inactive': CLINIC_INACTIVE_MESSAGE }

const CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/user-disabled',
  'auth/invalid-email',
  'auth/invalid-login-credentials',
])

export interface SignInPageProps {
  siteName: string
  /** `?reason=`: `session-expired` (A-06) or `reauth` (A-04 asked to sign in again). */
  reason?: string | undefined
  /** `?redirect=`: a same-origin path; anything else is ignored (T-A06-02). */
  redirect?: string | undefined
  /** A line under the title: the clinic app's impersonation hand-off (Frontend §6.4 step 2). */
  hint?: string | undefined
}

/** A-01 Sign in (UserFlows A-01, Frontend §5.2). */
export function SignInPage({ siteName, reason, redirect, hint }: SignInPageProps) {
  const store = useAuthStore()
  const queryClient = useQueryClient()
  const router = useRouter()
  const id = useId()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Bumped after a successful sign-in: the password is hidden again (UserFlows §3). */
  const [signedIn, setSignedIn] = useState(0)
  const banner = reason ? REASONS[reason] : undefined
  const errorBanner = reason ? ERROR_REASONS[reason] : undefined

  async function submit(e: SyntheticEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    store.setSigningIn(true)
    try {
      try {
        await store.signIn(email.trim(), password)
      } catch (err) {
        const code = isAuthError(err) ? err.code : ''
        setError(
          code === 'auth/too-many-requests'
            ? SIGN_IN_TOO_MANY
            : CREDENTIAL_CODES.has(code)
              ? SIGN_IN_GENERIC_ERROR
              : "Couldn't sign in. Check your connection and try again.",
        )
        return
      }
      // #12: a Firebase account we don't know (or a disabled user) gets the same generic message.
      queryClient.removeQueries({ queryKey: meQueryKey() })
      try {
        await queryClient.query(meQueryOptions())
      } catch (err) {
        if (isApiError(err) && err.code === 'unauthorized') {
          await store.signOut().catch(() => undefined)
          queryClient.clear()
          setError(SIGN_IN_GENERIC_ERROR)
          return
        }
        // Anything else: the guards on the next page handle it (A-04, no access).
      }
      setSignedIn((n) => n + 1)
      await router.navigate({ href: safeRedirect(redirect), replace: true })
    } finally {
      store.setSigningIn(false)
      setBusy(false)
    }
  }

  return (
    <AuthCard siteName={siteName}>
      <div>
        <AuthTitle>Sign in</AuthTitle>
      </div>
      {hint && <p className="muted">{hint}</p>}
      {banner && <AuthBanner>{banner}</AuthBanner>}
      {errorBanner && !error && <AuthBanner kind="error">{errorBanner}</AuthBanner>}
      {error && <AuthBanner kind="error">{error}</AuthBanner>}
      <form onSubmit={(e) => void submit(e)} noValidate>
        <div className="field">
          <label className="label" htmlFor={`${id}-email`}>
            Email
          </label>
          <input
            id={`${id}-email`}
            className="input"
            type="email"
            autoComplete="username"
            required
            aria-required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor={`${id}-password`}>
            Password
          </label>
          <PasswordField
            id={`${id}-password`}
            autoComplete="current-password"
            resetKey={signedIn}
            required
            aria-required
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={busy || email.trim() === '' || password === ''}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <Link to="/forgot-password" className="auth-link">
        Forgot password?
      </Link>
    </AuthCard>
  )
}
